<template>
    <page-content
        :loading="isLoading"
        :error="error"
    >
        <v-container>
            <v-row align="center" justify="center">
                <v-col xs="12" md="6" lg="6" class="pa-4">
                    <router-link
                        :to="{name: 'profile', params: { handle: $route.params.handle }}"
                    >
                        Back to Profile
                    </router-link>
                    <v-card class="pa-3 mt-2">
                        <h2 class="display-1 mb-2">
                            Edit Links
                        </h2>
                        <div class="d-flex mb-3">
                            <!-- Reorder Links -->
                            <v-dialog
                                v-model="reorderDialog.show"
                                :fullscreen="$vuetify.breakpoint.smAndDown"
                                :max-width="500"
                                :transition="$vuetify.breakpoint.smAndDown
                                    ? 'dialog-bottom-transition'
                                    : 'fade-transition'"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        text
                                        :small="$vuetify.breakpoint.xsOnly"
                                        @click="onReorderClick"
                                        v-on="on"
                                    >
                                        <v-icon left>
                                            reorder
                                        </v-icon>
                                        Reorder
                                    </v-btn>
                                </template>
                                <reorder-form
                                    title="Reorder Links"
                                    :show.sync="reorderDialog.show"
                                    :is-saving="reorderDialog.isSaving"
                                    :links="reorderDialog.links"
                                    @submit="onReorderSubmit"
                                />
                            </v-dialog>
                            <v-spacer />
                            <!-- Add Link -->
                            <v-dialog
                                v-model="addDialog.show"
                                :fullscreen="$vuetify.breakpoint.smAndDown"
                                :max-width="500"
                                :transition="$vuetify.breakpoint.smAndDown
                                    ? 'dialog-bottom-transition'
                                    : 'fade-transition'"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        color="primary"
                                        dark
                                        v-on="on"
                                        :small="$vuetify.breakpoint.xsOnly"
                                    >
                                        <v-icon :left="$vuetify.breakpoint.smAndUp">
                                            add
                                        </v-icon>
                                        Add
                                    </v-btn>
                                </template>
                                <link-form
                                    title="Add Link"
                                    :show.sync="addDialog.show"
                                    :is-saving="addDialog.isSaving"
                                    :link="addDialog.link"
                                    @submit="onAddSubmit"
                                />
                            </v-dialog>
                        </div>
                        <v-divider class="my-3" />
                        <p v-if="links.length !== 0">
                            Click a link below to begin editing.
                        </p>
                        <div
                            v-for="item in links"
                            :key="item.id"
                            class="mb-2"
                        >
                            <link-button
                                :link="item"
                                disable-link
                                @click="onEditClick(item)"
                            />
                        </div>
                        <div
                            v-if="links.length === 0"
                            class="text-center"
                        >
                            No Links found. Make one today!
                        </div>
                        <!-- Edit Dialog -->
                        <v-dialog
                            v-model="editDialog.show"
                            :fullscreen="$vuetify.breakpoint.smAndDown"
                            :max-width="500"
                            :transition="$vuetify.breakpoint.smAndDown
                                ? 'dialog-bottom-transition'
                                : 'fade-transition'"
                        >
                            <link-form
                                title="Edit Link"
                                :show.sync="editDialog.show"
                                :is-saving="editDialog.isSaving"
                                :link="editDialog.link"
                                has-delete
                                @submit="onEditSubmit"
                                @delete="onDeleteSubmit"
                            />
                        </v-dialog>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </page-content>
</template>

<script>
import axios from 'axios'
import { defaultError } from '../../utils/constants/errors'
import LinkButton from './LinkButton'
import LinkForm from './LinkForm'
import ReorderForm from './ReorderForm'
import MessageBus from '../../utils/buses/MessageBus'
import uniqueId from 'lodash/uniqueId'

export default {
    components: {
        LinkButton,
        LinkForm,
        ReorderForm
    },
    data () {
        return {
            isLoading: true,
            isSaving: false,
            error: null,
            links: [],
            addDialog: {
                show: false,
                isSaving: false,
                link: {}
            },
            editDialog: {
                show: false,
                isSaving: false,
                link: {} // passed in link to edit
            },
            reorderDialog: {
                show: false,
                isSaving: false,
                links: []
            }
        }
    },
    created () {
        const handle = this.$route.params.handle
        axios.get('/user/' + handle)
            .then(res => {
                // add unique id to links
                let links = res.data.user.links.map(i => ({
                    ...i,
                    id: uniqueId()
                }))

                this.links = [...links]
            })
            .catch(err => {
                console.error('Get User Error: ', err)
                this.error = defaultError
            })
            .finally(res => {
                this.isLoading = false
            })
    },
    methods: {
        onEditClick (link) {
            this.editDialog.link = link
            this.editDialog.show = true
        },
        onAddSubmit (link) {
            this.addDialog.isSaving = true

            const links = [
                {
                    text: link.text,
                    url: link.url,
                    type: link.type,
                    id: uniqueId()
                },
                ...this.links
            ]

            axios.put('/user', {
                links
            })
                .then(res => {
                    // update links with passed in value
                    // we do this instead of res to keep the lodash ids
                    this.links = links
                    MessageBus.sendMessage('Link added successfully', 'success')
                    this.addDialog.show = false
                    this.addDialog.link = {}
                })
                .catch(err => {
                    console.error('Link Add Error', err)
                    MessageBus.sendMessage(defaultError, 'error')
                })
                .finally(() => {
                    this.addDialog.isSaving = false
                })
        },
        onEditSubmit (link) {
            const id = this.editDialog.link.id
            const index = this.links.findIndex(item => item.id === id)

            if (index === -1) {
                MessageBus.sendMessage('Link not found', 'error')
                return
            }

            const links = [
                ...this.links
            ]
            // update link
            links[index] = {
                ...link,
                id
            }

            this.editDialog.isSaving = true

            axios.put('/user', {
                links
            })
                .then(res => {
                    this.links = links
                    MessageBus.sendMessage('Linked edited successfully', 'success')
                    this.editDialog.show = false
                })
                .catch(err => {
                    console.error('Link Edit Error', err)
                    MessageBus.sendMessage(defaultError, 'error')
                })
                .finally(() => {
                    this.editDialog.isSaving = false
                })
        },
        onDeleteSubmit () {
            const id = this.editDialog.link.id
            const index = this.links.findIndex(item => item.id === id)

            if (index === -1) {
                MessageBus.sendMessage('Link not found', 'error')
                return
            }

            const links = [
                ...this.links
            ]

            // remove link
            links.splice(index, 1)

            this.editDialog.isSaving = true
            axios.put('/user', {
                links
            })
                .then(res => {
                    this.links = links
                    MessageBus.sendMessage('Linked deleted successfully', 'success')
                    this.editDialog.show = false
                })
                .catch(err => {
                    console.error('Link Delete Error', err)
                    MessageBus.sendMessage(defaultError, 'error')
                })
                .finally(() => {
                    this.editDialog.isSaving = false
                })
        },
        onReorderClick () {
            this.reorderDialog.links = [...this.links]
        },
        onReorderSubmit (links) {
            this.reorderDialog.isSaving = true
            axios.put('/user', {
                links
            })
                .then(res => {
                    this.links = [...links]
                    MessageBus.sendMessage('Links reordered successfully', 'success')
                    this.reorderDialog.show = false
                })
                .catch(err => {
                    console.error('Link Reorder Error', err)
                    MessageBus.sendMessage(defaultError, 'error')
                })
                .finally(() => {
                    this.reorderDialog.isSaving = false
                })
        }
    }
}
</script>
